import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Button, Input, Space, Tag, message, Row, Col, Checkbox, Empty, Alert,
  Spin, Tooltip, Popconfirm, Modal, Form, Drawer, Divider, Dropdown, Progress,
} from 'antd';
import {
  FireOutlined, SearchOutlined, ReloadOutlined, YoutubeOutlined, CopyOutlined, PlusOutlined,
  DeleteOutlined, RiseOutlined, EyeOutlined, ClockCircleOutlined, CloseOutlined, DownloadOutlined,
  LikeOutlined, TagsOutlined, NumberOutlined, BulbOutlined, DownOutlined, UpOutlined,
  InfoCircleOutlined, ThunderboltOutlined, FieldTimeOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text, Paragraph } = Typography;

const fmt = (n) => {
  n = Number(n) || 0;
  if (n >= 1e7) return (n / 1e7).toFixed(2).replace(/\.?0+$/, '') + ' Cr';
  if (n >= 1e5) return (n / 1e5).toFixed(2).replace(/\.?0+$/, '') + ' L';
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
};

const scoreColor = (s) => (s >= 80 ? '#ef4444' : s >= 60 ? '#f97316' : s >= 40 ? '#eab308' : '#94a3b8');
const scoreLabel = (s) => (s >= 80 ? 'Viral' : s >= 60 ? 'Bahut tez' : s >= 40 ? 'Badh raha' : 'Normal');

function copyText(t, what = 'Copy') {
  if (!t) return;
  navigator.clipboard.writeText(t).then(() => message.success(`${what} ho gaya`)).catch(() => message.error('Copy nahi hua'));
}

const ageText = (h) => {
  h = Number(h) || 0;
  if (!h) return '';
  if (h < 24) return `${Math.round(h)} ghante pehle`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d} din pehle`;
  if (d < 30) return `${Math.round(d / 7)} hafte pehle`;
  return `${Math.round(d / 30)} mahine pehle`;
};

const WHY_ICON = {
  new: <ClockCircleOutlined style={{ color: '#2563eb' }} />,
  fire: <FireOutlined style={{ color: '#ef4444' }} />,
  like: <LikeOutlined style={{ color: '#16a34a' }} />,
  tag: <TagsOutlined style={{ color: '#7c3aed' }} />,
  hash: <NumberOutlined style={{ color: '#7c3aed' }} />,
  title: <BulbOutlined style={{ color: '#d97706' }} />,
  short: <ThunderboltOutlined style={{ color: '#db2777' }} />,
  long: <FieldTimeOutlined style={{ color: '#0891b2' }} />,
  search: <SearchOutlined style={{ color: '#2563eb' }} />,
  info: <InfoCircleOutlined style={{ color: '#6b7280' }} />,
};

/** Clickable chip: one click = copy. */
function CopyChip({ text, color }) {
  return (
    <Tooltip title="Click = copy">
      <Tag color={color} onClick={() => copyText(text, `"${text}" copy`)}
        style={{ cursor: 'pointer', fontSize: 13, padding: '3px 10px', marginBottom: 6, borderRadius: 8, userSelect: 'text' }}>
        {text}
      </Tag>
    </Tooltip>
  );
}

function VideoCard({ v, rank, active, onOpen }) {
  return (
    <Card size="small" hoverable onClick={() => onOpen(v)} bodyStyle={{ padding: 10 }}
      style={{ borderRadius: 12, height: '100%', border: active ? '2px solid #ef4444' : '1px solid #e5e7eb', boxShadow: active ? '0 0 0 3px #fee2e2' : undefined, cursor: 'pointer' }}
      cover={(
        <div style={{ position: 'relative', display: 'block' }}>
          <img alt="" src={v.thumbnail} style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', borderRadius: '12px 12px 0 0' }} />
          <span style={{ position: 'absolute', top: 6, left: 6, background: '#111827cc', color: '#fff', padding: '1px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>#{rank}</span>
          <span style={{ position: 'absolute', top: 6, right: 6, background: scoreColor(v.viralScore), color: '#fff', padding: '1px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
            <FireOutlined /> {v.viralScore}
          </span>
          {v.duration && <span style={{ position: 'absolute', bottom: 6, right: 6, background: '#111827cc', color: '#fff', padding: '0 6px', borderRadius: 4, fontSize: 11 }}>{v.duration}</span>}
        </div>
      )}
    >
      <div style={{ color: '#111827', fontWeight: 600, fontSize: 13, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 36, lineHeight: '18px' }}>{v.title}</div>
      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.channel}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: '#374151' }}>
        <span><EyeOutlined /> {fmt(v.views)}</span>
        <span><RiseOutlined /> {fmt(v.velocity)}/hr</span>
        <span><ClockCircleOutlined /> {v.publishedText}</span>
      </div>
      {v.tags && v.tags.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#7c3aed', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <TagsOutlined /> {v.tags.slice(0, 4).join(' · ')}
        </div>
      )}
    </Card>
  );
}

/** Side panel with everything about one video. */
function VideoDrawer({ video, query, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!video) return;
    let alive = true;
    setDetail(null); setErr(''); setLoading(true);
    api.get(`/trending/video/${video.videoId}`, { params: { q: query || '' } })
      .then(r => { if (alive) setDetail(r.data); })
      .catch(e => { if (alive) setErr(e.response?.data?.error || 'Detail load nahi hui'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [video, query]);

  if (!video) return null;
  const d = detail || {};
  const title = d.title || video.title;
  const tags = d.tags || video.tags || [];
  const hashtags = d.hashtags || [];
  const views = d.views || video.views;
  const velocity = d.velocity || video.velocity;
  const token = localStorage.getItem('token') || '';
  const thumbDl = (size) => {
    const name = encodeURIComponent((title || video.videoId).slice(0, 60));
    window.location.href = `${api.defaults.baseURL}/trending/video/${video.videoId}/thumb?size=${size}&name=${name}&token=${encodeURIComponent(token)}`;
  };
  const thumbItems = (d.thumbnails || [
    { key: 'maxresdefault', label: '1280x720 (HD)' }, { key: 'hqdefault', label: '480x360' },
  ]).map(t => ({ key: t.key, label: t.label, onClick: () => thumbDl(t.key) }));

  const hashFromTags = tags.slice(0, 12).map(t => '#' + t.replace(/[^\p{L}\p{M}\p{N}]+/gu, ''));
  const allHash = hashtags.length ? hashtags : hashFromTags;

  return (
    <Drawer
      open
      onClose={onClose}
      width={Math.min(560, window.innerWidth)}
      closeIcon={<CloseOutlined />}
      title={<Space><FireOutlined style={{ color: '#ef4444' }} /> Video detail</Space>}
      extra={<Button icon={<CloseOutlined />} onClick={onClose}>Band karo</Button>}
      bodyStyle={{ padding: 16, background: '#f8fafc' }}
    >
      <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 14px #0001' }}>
        <img alt="" src={`https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`} style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />
        <span style={{ position: 'absolute', top: 8, right: 8, background: scoreColor(video.viralScore), color: '#fff', padding: '2px 10px', borderRadius: 8, fontWeight: 700 }}>
          <FireOutlined /> {video.viralScore} · {scoreLabel(video.viralScore)}
        </span>
      </div>
      <Space wrap style={{ marginTop: 10 }}>
        <Dropdown menu={{ items: thumbItems }} trigger={['click']}>
          <Button type="primary" icon={<DownloadOutlined />}>Thumbnail download (HD) <DownOutlined /></Button>
        </Dropdown>
        <Button icon={<YoutubeOutlined />} danger href={video.url} target="_blank" rel="noreferrer">YouTube par kholo</Button>
        <Button icon={<CopyOutlined />} onClick={() => copyText(video.url, 'Link copy')}>Link copy</Button>
      </Space>

      <Title level={5} style={{ marginTop: 14, marginBottom: 4, lineHeight: 1.4 }}>{title} <CopyOutlined style={{ color: '#9ca3af', cursor: 'pointer' }} onClick={() => copyText(title, 'Title copy')} /></Title>
      <Text type="secondary">
        {d.channelUrl ? <a href={d.channelUrl} target="_blank" rel="noreferrer">{d.channel || video.channel}</a> : (d.channel || video.channel)}
        {d.category ? ` · ${d.category}` : ''}
        {d.publishedAt ? ` · ${new Date(d.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
      </Text>

      <Row gutter={8} style={{ marginTop: 12 }}>
        {[
          [<EyeOutlined />, 'Views', fmt(views)],
          [<RiseOutlined />, 'Views/ghanta', fmt(velocity)],
          [<LikeOutlined />, 'Likes', d.likes ? fmt(d.likes) : '—'],
          [<ClockCircleOutlined />, 'Upload', ageText(d.ageHours || video.ageHours) || video.publishedText],
        ].map(([ic, l, val]) => (
          <Col span={6} key={l}>
            <div style={{ background: '#fff', borderRadius: 10, padding: '8px 6px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{ic} {l}</div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{val}</div>
            </div>
          </Col>
        ))}
      </Row>
      <div style={{ marginTop: 10 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Viral score</Text>
        <Progress percent={video.viralScore} strokeColor={scoreColor(video.viralScore)} size="small" />
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 16 }}><Spin /> <Text type="secondary">YouTube se tags / description nikal rahe hain…</Text></div>}
      {err && <Alert type="warning" showIcon message={err} style={{ marginTop: 10 }} />}

      <Divider orientation="left" style={{ margin: '16px 0 10px' }}><BulbOutlined /> Kyun trending hai?</Divider>
      {(d.why || []).length ? (
        <div style={{ background: '#fff', borderRadius: 10, padding: 10, border: '1px solid #e5e7eb' }}>
          {d.why.map((w, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '5px 0', borderTop: i ? '1px dashed #f1f5f9' : 'none', fontSize: 13 }}>
              <span style={{ fontSize: 16 }}>{WHY_ICON[w.icon] || WHY_ICON.info}</span><span>{w.text}</span>
            </div>
          ))}
        </div>
      ) : !loading && <Text type="secondary">—</Text>}

      <Divider orientation="left" style={{ margin: '16px 0 10px' }}><NumberOutlined /> Hashtags ({allHash.length})</Divider>
      <div style={{ background: '#fff', borderRadius: 10, padding: 10, border: '1px solid #e5e7eb' }}>
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" type="primary" icon={<CopyOutlined />} disabled={!allHash.length} onClick={() => copyText(allHash.join(' '), 'Saare hashtags copy')}>Saare copy</Button>
          {!hashtags.length && tags.length > 0 && <Text type="secondary" style={{ fontSize: 11 }}>(video me hashtag nahi the — tags se banaye)</Text>}
        </Space>
        <div>{allHash.length ? allHash.map(h => <CopyChip key={h} text={h} color="purple" />) : <Text type="secondary">Koi hashtag nahi</Text>}</div>
      </div>

      <Divider orientation="left" style={{ margin: '16px 0 10px' }}><TagsOutlined /> Tags ({tags.length})</Divider>
      <div style={{ background: '#fff', borderRadius: 10, padding: 10, border: '1px solid #e5e7eb' }}>
        <Space style={{ marginBottom: 8 }} wrap>
          <Button size="small" type="primary" icon={<CopyOutlined />} disabled={!tags.length} onClick={() => copyText(tags.join(', '), 'Saare tags copy')}>Saare copy (comma)</Button>
          <Button size="small" icon={<CopyOutlined />} disabled={!tags.length} onClick={() => copyText(tags.join('\n'), 'Tags copy')}>Line-wise copy</Button>
        </Space>
        <div>{tags.length ? tags.map(t => <CopyChip key={t} text={t} color="geekblue" />) : <Text type="secondary">{loading ? '…' : 'Is video me custom tags nahi hain'}</Text>}</div>
      </div>

      {d.description && (
        <>
          <Divider orientation="left" style={{ margin: '16px 0 10px' }}>Description <CopyOutlined style={{ color: '#9ca3af', cursor: 'pointer' }} onClick={() => copyText(d.description, 'Description copy')} /></Divider>
          <Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#fff', borderRadius: 10, padding: 10, border: '1px solid #e5e7eb', maxHeight: 220, overflow: 'auto' }}>{d.description}</Paragraph>
        </>
      )}
    </Drawer>
  );
}

function Board({ snap, loading, onRefresh, onClose, onOpenVideo, activeId, onMore, moreLoading, collapsed, onToggle }) {
  if (loading && !snap) {
    return (
      <Card style={{ borderRadius: 14, marginBottom: 16 }}>
        <div style={{ textAlign: 'center', padding: 30 }}><Spin size="large" /><div style={{ marginTop: 12, color: '#6b7280' }}>YouTube se trending nikal rahe hain…</div></div>
      </Card>
    );
  }
  if (!snap) return null;
  const ins = snap.insight || {};
  const vids = snap.videos || [];
  const done = snap.done || !snap.cont;
  return (
    <Card
      style={{ borderRadius: 14, marginBottom: 16, borderTop: '4px solid #ef4444' }}
      bodyStyle={{ padding: collapsed ? 0 : 16 }}
      title={(
        <Space wrap>
          <FireOutlined style={{ color: '#ef4444' }} />
          <span style={{ fontWeight: 700, fontSize: 16 }}>{snap.label}</span>
          <Tag color="red">{vids.length} videos</Tag>
          <Tag>last {snap.windowDays || 14} din</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>"{snap.query}" · {new Date(snap.updatedAt).toLocaleString('en-IN')}{snap.stale ? ' · purana data' : ''}</Text>
        </Space>
      )}
      extra={(
        <Space>
          <Button size="small" icon={collapsed ? <DownOutlined /> : <UpOutlined />} onClick={onToggle}>{collapsed ? 'Kholo' : 'Chhota karo'}</Button>
          <Button size="small" icon={<ReloadOutlined spin={loading} />} onClick={onRefresh} disabled={loading}>Refresh</Button>
          {onClose && <Tooltip title="Band karo"><Button size="small" danger icon={<CloseOutlined />} onClick={onClose} /></Tooltip>}
        </Space>
      )}
    >
      {!collapsed && (
        <>
          {snap.error && <Alert type="warning" showIcon message={snap.error} style={{ marginBottom: 12 }} />}
          {ins.recommendation && <Alert type="info" showIcon icon={<BulbOutlined />} message={ins.recommendation} style={{ marginBottom: 14 }} />}
          {vids.length ? (
            <Row gutter={[12, 12]}>
              {vids.map((v, i) => (
                <Col key={v.videoId} xs={24} sm={12} md={8} lg={6} xxl={4}>
                  <VideoCard v={v} rank={i + 1} active={activeId === v.videoId} onOpen={onOpenVideo} />
                </Col>
              ))}
            </Row>
          ) : <Empty description="Last 2 hafte me is category ka koi trending video nahi mila" />}
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            {done
              ? <Text type="secondary">Bas — YouTube ne aur videos nahi diye ({vids.length} loaded)</Text>
              : <Button type="primary" size="large" ghost loading={moreLoading} icon={<DownOutlined />} onClick={onMore}>Aur 100 videos load karo</Button>}
          </div>
          {(ins.topTags?.length || ins.topHashtags?.length) ? (
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col xs={24} md={12}>
                <Card size="small" title={<><TagsOutlined /> Trending tags (top videos ke asli tags)</>} extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copyText((ins.topTags || []).map(t => t.tag).join(', '), 'Tags copy')}>Sab copy</Button>}>
                  <div>{(ins.topTags || []).map(t => <CopyChip key={t.tag} text={t.tag} color="geekblue" />)}</div>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title={<><NumberOutlined /> Hashtags + title words</>} extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copyText((ins.topHashtags || []).map(t => t.tag).join(' '), 'Hashtags copy')}>Sab copy</Button>}>
                  <div>{(ins.topHashtags || []).map(t => <CopyChip key={t.tag} text={t.tag} color="purple" />)}</div>
                  <div style={{ marginTop: 6 }}>{(ins.titleWords || []).map(w => <CopyChip key={w.word} text={w.word} />)}</div>
                </Card>
              </Col>
            </Row>
          ) : null}
        </>
      )}
    </Card>
  );
}

export default function Trending() {
  const [cats, setCats] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem('trending_cats') || '[]'); } catch (e) { return []; }
  });
  const [snaps, setSnaps] = useState({});
  const [loading, setLoading] = useState({});
  const [moreLoading, setMoreLoading] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [q, setQ] = useState('');
  const [searchSnap, setSearchSnap] = useState(null);
  const [searching, setSearching] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [active, setActive] = useState(null); // { video, query }

  const loadCats = useCallback(async () => {
    try {
      const r = await api.get('/trending/categories');
      setCats(r.data.categories || []);
      setIsAdmin(!!r.data.is_admin);
    } catch (e) { message.error(e.response?.data?.error || 'Categories load nahi hui'); }
  }, []);
  useEffect(() => { loadCats(); }, [loadCats]);

  const loadCat = useCallback(async (id, refresh = false) => {
    setLoading(l => ({ ...l, [id]: true }));
    try {
      const r = await api.get(`/trending/category/${id}${refresh ? '?refresh=1' : ''}`);
      setSnaps(s => ({ ...s, [id]: r.data }));
    } catch (e) {
      message.error(e.response?.data?.error || 'Trending load nahi hua');
    } finally { setLoading(l => ({ ...l, [id]: false })); }
  }, []);

  useEffect(() => {
    localStorage.setItem('trending_cats', JSON.stringify(selected));
    selected.forEach(id => { if (!snaps[id] && !loading[id]) loadCat(id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const toggle = (id, on) => setSelected(sel => (on ? [...new Set([...sel, id])] : sel.filter(x => x !== id)));

  const mergeMore = (snap, r) => {
    const seen = new Set((snap.videos || []).map(v => v.videoId));
    const add = (r.videos || []).filter(v => !seen.has(v.videoId));
    return { ...snap, videos: [...(snap.videos || []), ...add], cont: r.cont, done: r.done };
  };

  const loadMore = async (key, snap, setter) => {
    if (!snap?.cont) return;
    setMoreLoading(m => ({ ...m, [key]: true }));
    try {
      const r = await api.post('/trending/more', { cont: snap.cont, count: 100 }, { timeout: 150000 });
      setter(mergeMore(snap, r.data));
      if (!r.data.videos?.length && !r.data.done) message.info('Is page par 2 hafte se naya kuch nahi tha — phir se "Aur" dabao');
    } catch (e) { message.error(e.response?.data?.error || 'Aur load nahi hua'); } finally { setMoreLoading(m => ({ ...m, [key]: false })); }
  };

  const doSearch = async (refresh = false) => {
    const query = q.trim();
    if (!query) { message.warning('Singer / gaana / keyword likho'); return; }
    setSearching(true);
    try {
      const r = await api.post('/trending/search', { query, refresh });
      setSearchSnap(r.data);
      setCollapsed(c => ({ ...c, search: false }));
    } catch (e) { message.error(e.response?.data?.error || 'Search fail'); } finally { setSearching(false); }
  };
  const closeSearch = () => { setSearchSnap(null); setQ(''); setActive(null); };

  const addCat = async (v) => {
    try {
      await api.post('/trending/categories', v);
      message.success('Category ban gayi');
      setAddOpen(false); addForm.resetFields(); loadCats();
    } catch (e) { message.error(e.response?.data?.error || 'Nahi bani'); }
  };
  const delCat = async (id) => {
    try {
      await api.delete(`/trending/categories/${id}`);
      setSelected(sel => sel.filter(x => x !== id));
      loadCats();
    } catch (e) { message.error(e.response?.data?.error || 'Nahi hati'); }
  };

  const visible = cats.filter(c => selected.includes(c.id));
  const totalVideos = visible.reduce((n, c) => n + (snaps[c.id]?.videos?.length || 0), 0) + (searchSnap?.videos?.length || 0);
  const openVideo = (query) => (v) => setActive({ video: v, query });

  return (
    <div>
      <div style={{ background: 'linear-gradient(135deg, #111827 0%, #7f1d1d 100%)', borderRadius: 16, padding: '18px 22px', marginBottom: 16, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#fff', fontFamily: "'Poppins', sans-serif" }}><FireOutlined style={{ color: '#f87171' }} /> YouTube Trending</Title>
          <div style={{ opacity: 0.85, marginTop: 4 }}>Sirf <b>last 2 hafte</b> ke sabse tez badhte videos. Kisi video par click karo → side me tags, hashtags, thumbnail download aur "kyun trending hai" khul jayega.</div>
        </div>
        <Space size="large">
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 700 }}>{visible.length + (searchSnap ? 1 : 0)}</div><div style={{ fontSize: 12, opacity: 0.8 }}>Sections</div></div>
          <div style={{ textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 700 }}>{totalVideos}</div><div style={{ fontSize: 12, opacity: 0.8 }}>Videos loaded</div></div>
        </Space>
      </div>

      <Card style={{ borderRadius: 14, marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
        <Space.Compact style={{ width: '100%', maxWidth: 760 }}>
          <Input size="large" prefix={<SearchOutlined />} placeholder="Singer, gaana, keyword ya channel — jaise: Rasiya, Khatu Shyam bhajan, Sapna Choudhary…"
            value={q} onChange={e => setQ(e.target.value)} onPressEnter={() => doSearch()} allowClear />
          <Button size="large" type="primary" icon={<FireOutlined />} loading={searching} onClick={() => doSearch()}>Trend dekho</Button>
          {searchSnap && <Button size="large" danger icon={<CloseOutlined />} onClick={closeSearch}>Search band</Button>}
        </Space.Compact>
        {searchSnap && (
          <div style={{ marginTop: 8 }}>
            <Button type="link" size="small" icon={<ArrowLeftOutlined />} onClick={closeSearch} style={{ padding: 0 }}>Search hatao, wapas categories par jao</Button>
          </div>
        )}
      </Card>

      {(searchSnap || searching) && (
        <Board
          snap={searchSnap} loading={searching}
          onRefresh={() => doSearch(true)} onClose={closeSearch}
          onOpenVideo={openVideo(searchSnap?.query)} activeId={active?.video?.videoId}
          onMore={() => loadMore('search', searchSnap, setSearchSnap)} moreLoading={!!moreLoading.search}
          collapsed={!!collapsed.search} onToggle={() => setCollapsed(c => ({ ...c, search: !c.search }))}
        />
      )}

      <Card
        style={{ borderRadius: 14, marginBottom: 16 }}
        title={<span><YoutubeOutlined style={{ color: '#ef4444' }} /> Categories — tick karo</span>}
        extra={isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Nayi Category</Button>}
      >
        <Space wrap size={[10, 10]}>
          {cats.map(c => (
            <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid', borderColor: selected.includes(c.id) ? '#ef4444' : '#e5e7eb', background: selected.includes(c.id) ? '#fef2f2' : '#fff', borderRadius: 999, padding: '6px 12px' }}>
              <Checkbox checked={selected.includes(c.id)} onChange={e => toggle(c.id, e.target.checked)}>
                <b>{c.label}</b> <Text type="secondary" style={{ fontSize: 11 }}>({c.query})</Text>
              </Checkbox>
              {loading[c.id] && <Spin size="small" />}
              {isAdmin && (
                <Popconfirm title={`"${c.label}" category hatani hai?`} onConfirm={() => delCat(c.id)} okText="Haan" cancelText="Nahi">
                  <Tooltip title="Category hatao"><DeleteOutlined style={{ color: '#9ca3af', cursor: 'pointer' }} /></Tooltip>
                </Popconfirm>
              )}
            </span>
          ))}
          {cats.length > 0 && (
            <>
              <Button size="small" onClick={() => setSelected(cats.map(c => c.id))}>Sab tick</Button>
              <Button size="small" onClick={() => setSelected([])}>Sab hatao</Button>
            </>
          )}
        </Space>
      </Card>

      {visible.length === 0 && !searchSnap && (
        <Empty description="Upar koi category tick karo — us category ke trending videos yahan dikhenge" style={{ padding: 40 }} />
      )}
      {visible.map(c => (
        <Board
          key={c.id} snap={snaps[c.id]} loading={!!loading[c.id]}
          onRefresh={() => loadCat(c.id, true)} onClose={() => toggle(c.id, false)}
          onOpenVideo={openVideo(c.query)} activeId={active?.video?.videoId}
          onMore={() => loadMore(c.id, snaps[c.id], (s) => setSnaps(all => ({ ...all, [c.id]: s })))} moreLoading={!!moreLoading[c.id]}
          collapsed={!!collapsed[c.id]} onToggle={() => setCollapsed(col => ({ ...col, [c.id]: !col[c.id] }))}
        />
      ))}

      <VideoDrawer video={active?.video} query={active?.query} onClose={() => setActive(null)} />

      <Modal title="Nayi Trending Category" open={addOpen} onCancel={() => setAddOpen(false)} onOk={() => addForm.submit()} okText="Add">
        <Form form={addForm} layout="vertical" onFinish={addCat}>
          <Form.Item name="label" label="Category ka naam" rules={[{ required: true, message: 'Naam likho' }]}><Input placeholder="jaise: Meena Geet" /></Form.Item>
          <Form.Item name="query" label="YouTube par kya search kare (khali chhodo to naam hi)"><Input placeholder="jaise: new meena geet" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
